import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";

const saintJeanCapFerrat: ChauffeurPage = {
  slug: "saint-jean-cap-ferrat",
  title: "VTC & private chauffeur in Saint-Jean-Cap-Ferrat",
  subtitle:
    "Private chauffeur in Saint-Jean-Cap-Ferrat: villas and palaces of the peninsula, airport meet-and-greet and complete discretion.",
  keywords: [
    "vtc saint-jean-cap-ferrat",
    "private chauffeur saint-jean-cap-ferrat",
    "cap-ferrat chauffeur",
  ],
  hero: {
    title: "VTC & private chauffeur in Saint-Jean-Cap-Ferrat",
    subtitle:
      "The most discreet peninsula on the Riviera, between Nice and Monaco: a chauffeur for villas and palaces.",
    keywords: [
      "VTC Saint-Jean-Cap-Ferrat",
      "Cap-Ferrat chauffeur",
      "villa chauffeur peninsula",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Saint-Jean-Cap-Ferrat",
    badges: ["Nice airport welcome", "Villa drop-off", "UHNW discretion"],
  },
  benefits: {
    heading: "Why a chauffeur in Saint-Jean-Cap-Ferrat",
    items: [
      {
        title: "Discretion for a private clientele",
        description:
          "A peninsula of villas and wealthy estates: drop-off at the gate, no manoeuvring or hesitation, coordinated with concierge desks.",
      },
      {
        title: "Palaces of the peninsula",
        description:
          "Grand-Hôtel du Cap-Ferrat (Four Seasons), Royal-Riviera, La Voile d'Or: welcome and pick-up right at the hotel.",
      },
      {
        title: "Airport close by",
        description:
          "A quick link with Nice Côte d'Azur, around 20 to 30 minutes via the lower corniche, name-board welcome at both terminals.",
      },
      {
        title: "Between Nice and Monaco",
        description:
          "An ideal base to reach the Principality, Èze or the Old Town of Nice, with no parking constraints on the peninsula.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Saint-Jean-Cap-Ferrat",
    services: [
      {
        title: "Airport transfers",
        text: "Saint-Jean-Cap-Ferrat ↔ Nice airport with flight tracking and a name-board welcome, for guest arrivals and jet departures.",
      },
      {
        title: "Villa & palace drop-off",
        text: "Pick-up at the villa gate or at the door of the Grand-Hôtel du Cap-Ferrat, coordinated with caretakers and concierges.",
      },
      {
        title: "Hourly & daily hire",
        text: "A dedicated chauffeur for owners and families, from the Villa Ephrussi de Rothschild to dinners in Monaco or Beaulieu.",
      },
      {
        title: "Riviera links",
        text: "Monaco, Èze, Villefranche-sur-Mer and Nice: direct trips or a full day out from the peninsula.",
      },
      {
        title: "Paloma beach & coastal path",
        text: "Drop-off at Paloma beach and pick-up after the coastal-path walk, with no parking worries in season.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Villa Ephrussi de Rothschild",
        description:
          "Villa-museum and gardens, one of the peninsula's landmark sites.",
      },
      {
        name: "Grand-Hôtel du Cap-Ferrat",
        description:
          "Four Seasons palace, drop-off and pick-up at the hotel door.",
      },
      {
        name: "Paloma beach",
        description:
          "Sought-after cove on the eastern side, access to the coastal path.",
      },
      {
        name: "Harbour & village",
        description: "Marina and restaurants of the Saint-Jean village.",
      },
      {
        name: "Cap villas",
        description:
          "Private, confidential lanes, discreet drop-off at the gate.",
      },
      {
        name: "Neighbouring Beaulieu-sur-Mer",
        description: "Station and seafront adjoining the peninsula.",
      },
    ],
  },
  generic: {
    heading: "Saint-Jean-Cap-Ferrat, the peninsula of grand villas",
    subheading:
      "Between Nice and Monaco, a discreet cape where the chauffeur makes all the difference.",
    paragraphs: [
      "Saint-Jean-Cap-Ferrat lines its narrow lanes with some of the most coveted villas on the Riviera, from the Villa Ephrussi de Rothschild to the private estates of the Cap. A chauffeur who knows the access roads drops you right at the gate, with complete discretion and in coordination with caretakers and concierge desks.",
      "The peninsula is barely twenty minutes from Nice airport via the lower corniche. For a guest arrival, a jet departure or a dinner in Monaco, the same chauffeur strings the trips together with no parking constraints — a real concern in high season on the Cap.",
      "For a day on the peninsula — Villa Ephrussi, Paloma beach, a walk along the coastal path then dinner at the Grand-Hôtel du Cap-Ferrat — hourly hire keeps the car on hand from one point to the next.",
    ],
    bullets: [
      "T1/T2 flight tracking",
      "Drop-off at the gate",
      "Concierge coordination",
    ],
  },
  process: {
    heading: "Book a chauffeur in Saint-Jean-Cap-Ferrat",
    steps: [
      "Share the pick-up point (villa, palace, airport, Beaulieu station).",
      "We set the time based on the flight, guest arrivals or your programme.",
      "The chauffeur appears at the gate or hotel door, with complete discretion.",
      "Direct transfer or hourly hire for the day across the peninsula and the Riviera.",
    ],
  },
  faq: [
    {
      q: "How long is it from Nice airport to Saint-Jean-Cap-Ferrat?",
      a: "Around 20 to 30 minutes via the lower corniche, outside peak hours. We track the guests' flight to adjust the pick-up and allow a margin in season.",
    },
    {
      q: "Do you serve the private villas of Cap-Ferrat?",
      a: "Yes. We drop off and pick up directly at the villa gate, with complete discretion and in coordination with caretakers or the concierge if needed.",
    },
    {
      q: "Can you pick up at the Grand-Hôtel du Cap-Ferrat?",
      a: "Yes, we welcome and collect at the door of the Grand-Hôtel du Cap-Ferrat as well as the other establishments on the peninsula (Royal-Riviera, La Voile d'Or), liaising with the concierge.",
    },
    {
      q: "Is parking a problem on the peninsula in summer?",
      a: "The approaches to Paloma beach and the village fill up quickly in season. With a chauffeur, you are dropped as close as possible and collected on request, with no hunt for a space.",
    },
  ],
  relatedLinks: [
    {
      label: "Nice Airport ⇄ Saint-Jean-Cap-Ferrat, rate and duration",
      href: "/en/chauffeur/transfers/nice-airport-saint-jean-cap-ferrat",
    },
    {
      label: "Monaco ⇄ Nice Airport, rate and duration",
      href: "/en/chauffeur/transfers/monaco-nice-airport",
    },
    { label: "Routes between Riviera towns", href: "/en/chauffeur/transfers" },
  ],
  cta: {
    title: "Your private chauffeur in Saint-Jean-Cap-Ferrat",
    text: "Villa on the Cap, palace or arrival at Nice airport: book your chauffeur in complete discretion.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title:
      "Saint-Jean-Cap-Ferrat chauffeur — villas & palaces | KS Rent Services",
    description:
      "Private car service in Saint-Jean-Cap-Ferrat: Cap villas, Grand-Hôtel du Cap-Ferrat, Villa Ephrussi and Nice airport transfer. Discretion, 24/7.",
    canonical: "/en/chauffeur/saint-jean-cap-ferrat",
  },
};

export default saintJeanCapFerrat;
